import { useState, Suspense, lazy } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ScreenOverlay, Spinner } from "../../components/Common";
import {
    useJobFromQuote,
    useQuote,
    useQuoteDelete,
    useQuoteLineItems,
    useQuoteStatusUpdate
} from "../../common/hooks/api/quote";
import { toastError, toastSuccess } from "../../helpers/toast-helper";
import { getResponseItemData } from "../../helpers/response-helper";
import useAuthUser from "../../common/hooks/authUser";
import { Card, CardBody } from "reactstrap";
import useQuotePdf from "../../common/hooks/pdfPrepareData";
import { useLineItemCategoryList } from "../../common/hooks/api/line-item-category";
import { getArea } from "../../components/Common/MapAreaBox/helper";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import Geometry from "@arcgis/core/geometry/Geometry";
import { DEFAULT_LINE_ITEMS } from "../../constants/quote";

const QuoteDetails = lazy(() => import("./components/details"));

const QuoteDetailsPage = () => {
    const { id } = useParams();
    const { isAdmin, user } = useAuthUser();
    const [jobFromQuoteId, setJobFromQuoteId] = useState<string>('');
    const history = useNavigate();
    const queryClient = useQueryClient();

    const { data: quoteDetailsData, isLoading, queryKey: quoteQueryKey } = useQuote(id, {
        onError: () => {
            toastError("Cannot fetch the quote");
        }
    });

    const { data } = getResponseItemData(quoteDetailsData);
    const { mutate: updateQuoteStatus, isPending: isSaving } = useQuoteStatusUpdate();
    const { mutate: deleteQuote, isPending: isDeleting } = useQuoteDelete();
    const { isLoading: isCreateJobFromQuoteLoading } = useJobFromQuote(jobFromQuoteId, {
        enabled: !!jobFromQuoteId,
        onSuccess: async ({ data }) => {
            await toastSuccess("Job was successfully created. You will be redirected to details soon.");
            history(`/jobs/${data?.id}`);
            setJobFromQuoteId('');
        },
        onError: () => {
            toastError("Cannot create job from the quote");
            setJobFromQuoteId('');
        }
    });

    const { data: lineItemsCategoryData, isLoading: isQuoteLineCategoryDataLoading } = useLineItemCategoryList({});

    const { data: quoteLineItemsData, isLoading: isQuoteLineItemsLoading } = useQuoteLineItems(id, {
        enabled: !!id,
        onError: () => {
            toastError("Cannot fetch the quote line items");
        }
    });

    const { data: lineItemsCategoryList } = getResponseItemData(lineItemsCategoryData);
    const { data: quoteLineItems } = getResponseItemData(quoteLineItemsData);

    const { generatePdfAndDownload, isLoading: isPdfLoading } = useQuotePdf();

    const onStatusChanged = (quoteStatusId) => {
        const payload = {
            quoteId: +id!,
            quoteStatusId: quoteStatusId,
        };

        updateQuoteStatus(payload, {
            onSuccess: () => {
                toastSuccess("Quote status was successfully updated");
                queryClient.invalidateQueries({ queryKey: quoteQueryKey });
            },
            onError: async () => {
                toastError("Cannot update quote status");
            },
        });
    };

    const onDeleteClicked = () => {
        deleteQuote(+id!, {
            onSuccess: async () => {
                await toastSuccess("Quote was successfully deleted");
                history('/quotes');
            },
            onError: () => {
                toastError("Cannot delete the quote");
            },
        });
    };

    const onDownloadPDFClicked = () => {
        const totalArea = data?.geometry ? getArea((geometryEngine as any).union(data?.geometry as any as Geometry[])): 0;

        generatePdfAndDownload({
            lineItemsCategoryList: (lineItemsCategoryList || []),
            lineItems: quoteLineItems?.lineItems?.length ? quoteLineItems.lineItems : DEFAULT_LINE_ITEMS,
            quote: data!,
            totalArea,
        });
    };

    const isAdminOrOwner = isAdmin || (data?.userId === user.id);

    if (!isLoading && !isAdminOrOwner) {
        return (
            <Card className="mt-4">
                <CardBody>
                    You don&apos;t have permissions to see this quote details
                </CardBody>
            </Card>
        );
    }

    return (
        <>
            {!isLoading && (
                <Suspense>
                    <QuoteDetails
                        data={data}
                        isSaving={isSaving}
                        isDeleting={isDeleting}
                        isPDFButtonDisabled={isQuoteLineCategoryDataLoading || isQuoteLineItemsLoading || isPdfLoading}
                        onStatusChanged={onStatusChanged}
                        onDeleteClicked={onDeleteClicked}
                        onCreateJobFromQuoteClicked={setJobFromQuoteId}
                        onDownloadPDFClicked={onDownloadPDFClicked}
                    />
                </Suspense>
            )}

            {(isLoading || isCreateJobFromQuoteLoading) && (
                <>
                    <ScreenOverlay />
                    <Spinner />
                </>
            )}
        </>
    );
};

export default QuoteDetailsPage;
